import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { CheckCircle2, AlertTriangle } from 'lucide-react'
import type { AssistantChatResult } from '../api/assistant-api'

interface Props {
  result: AssistantChatResult
  accepted: boolean | null
  onAccept: () => void
  onReject: () => void
}

export function ProposedChangesCard({ result, accepted, onAccept, onReject }: Props) {
  const ops = result.patch || []
  const isNoop = ops.length === 0

  return (
    <div className="mt-2 rounded-md border border-border bg-card shadow-sm">
      <div className="flex flex-row items-center gap-2 p-3 border-b border-border">
        <span className="text-sm font-semibold">تغییرات پیشنهادی</span>
        {result.form_valid ? (
          <Badge variant="outline" className="text-emerald-400 border-emerald-500/40 gap-1 text-[10px]">
            <CheckCircle2 className="w-3 h-3" /> معتبر
          </Badge>
        ) : (
          <Badge variant="outline" className="text-destructive border-destructive/40 gap-1 text-[10px]">
            <AlertTriangle className="w-3 h-3" /> نامعتبر
          </Badge>
        )}
        <Badge variant="secondary" className="text-[10px]">{ops.length} عملیات</Badge>
        {result.tool_trace?.length > 0 && (
          <Badge variant="outline" className="text-[10px]">{result.tool_trace.length} ابزار</Badge>
        )}
      </div>
      <div className="p-3">
        <Tabs defaultValue="patch" dir="rtl">
          <TabsList className="h-8">
            <TabsTrigger value="patch" className="text-xs">پچ</TabsTrigger>
            <TabsTrigger value="form" className="text-xs">فرم نهایی</TabsTrigger>
            <TabsTrigger value="tools" className="text-xs">ابزارها</TabsTrigger>
            {!result.form_valid && <TabsTrigger value="errors" className="text-xs">خطاها</TabsTrigger>}
          </TabsList>
          <TabsContent value="patch">
            <JsonPre value={ops} className="max-h-72" />
          </TabsContent>
          <TabsContent value="form">
            <JsonPre value={result.updated_form} className="max-h-96" />
          </TabsContent>
          <TabsContent value="tools">
            <div className="flex flex-col gap-2 max-h-72 overflow-y-auto">
              {result.tool_trace.map((t, i) => (
                <div key={i} className="rounded-md border border-border p-2 text-xs">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-[10px]">{t.tool}</Badge>
                    {t.error
                      ? <Badge variant="outline" className="text-destructive border-destructive/40 text-[10px]">خطا</Badge>
                      : <Badge variant="outline" className="text-emerald-400 border-emerald-500/40 text-[10px]">ok</Badge>}
                    <span className="text-muted-foreground text-[10px]">تلاش {t.attempts}</span>
                  </div>
                  {t.summary && <div className="mt-1 text-muted-foreground">{t.summary}</div>}
                  {t.error && <div className="mt-1 text-destructive">{t.error}</div>}
                </div>
              ))}
              {result.tool_trace.length === 0 && (
                <div className="text-xs text-muted-foreground">ابزاری فراخوانی نشد.</div>
              )}
            </div>
          </TabsContent>
          {!result.form_valid && (
            <TabsContent value="errors">
              <ul className="text-xs flex flex-col gap-1 max-h-72 overflow-y-auto">
                {result.validation_report.issues.map((i, idx) => (
                  <li key={idx} className="border-r-2 border-destructive pr-2">
                    <span className="font-mono text-[10px]" dir="ltr">{i.path || '/'}</span> — {i.message}
                  </li>
                ))}
              </ul>
            </TabsContent>
          )}
        </Tabs>
      </div>
      <div className="flex items-center justify-end gap-2 p-3 border-t border-border">
        {accepted === null ? (
          <>
            <Button variant="outline" size="sm" onClick={onReject}>رد</Button>
            <Button size="sm" onClick={onAccept} disabled={isNoop || !result.form_valid}>
              اعمال تغییرات
            </Button>
          </>
        ) : accepted ? (
          <Badge variant="outline" className="text-emerald-400 border-emerald-500/40">اعمال شد</Badge>
        ) : (
          <Badge variant="secondary">رد شد</Badge>
        )}
      </div>
    </div>
  )
}

function JsonPre({ value, className }: { value: unknown; className?: string }) {
  return (
    <pre
      dir="ltr"
      className={`mt-2 text-[10px] font-mono whitespace-pre-wrap bg-muted/30 border border-border rounded-md p-2 overflow-auto ${className ?? ''}`}
    >
      {JSON.stringify(value, null, 2)}
    </pre>
  )
}
